import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';

import { SvgMatType } from '../constants/icons';
import { SessionStatus } from '../constants/session';

import { SessionService } from './storage/session.service';
import { ApiService } from './api.service';

import { BenefitResourceLinks, ResourceDocuments, ResourceLink } from '../models/resource-link';
import { Form1095EmailCriteria, Form1095Model } from '../models/form-1095';
import { ApiHeaderType } from '../constants/api-header-type';
import { ToastType } from '../constants/toast-type';
import { ToastService } from './toast.service';
import { EnvironmentType } from '../constants/environment-type';
import { environment } from 'src/environments/environment';
import { W2EmailCriteria, W2Model } from '../models/w2';

const controllerRoute = '/myresources';

@Injectable()
export class DocumentService {

    public benefitResources: ResourceLink[];
    public companyResources: ResourceLink[];
    public complianceResources: ResourceLink[];

    public storageStore: Observable<any>;

    constructor(
        private apiService: ApiService,
        private sessionService: SessionService,
        private toastService: ToastService,
    ) {
        this.storageStore = this.sessionService.$SessionClear;
        this.storageStore.subscribe((status: string) => {
            switch (status) {
                case SessionStatus.clearMember:
                case SessionStatus.clearSession:
                    this.benefitResources = null;
                    this.companyResources = null;
                    this.complianceResources = null;
                    break;
            }
        });
    }

    public getResouceIcon(resources: ResourceLink[]): ResourceLink[] {
        resources.forEach(resource => {
            resource.fileType = resource.fileType.toLowerCase();
            switch (resource.fileType) {
                case 'pdf':
                    resource.fileType = SvgMatType.Pdf;
                    break;
                case 'mp4':
                    resource.fileType = SvgMatType.VideoLibrary;
                    break;
                default:
                    resource.fileType = SvgMatType.ExternalLink;
                    break;
            }
        });

        return resources;
    }

    public getBenefitResources(): Observable<ResourceLink[]> {
        if (this.benefitResources)
            return of(this.benefitResources);

        return this.apiService.get(`${controllerRoute}/benefit-level`).pipe(
            map((response: BenefitResourceLinks) => {
                this.benefitResources = response.benefitDocuments;
                return response.benefitDocuments;
            }),
        );
    }

    public getCompanyResources(): Observable<ResourceLink[]> {
        if (this.companyResources)
            return of(this.companyResources);

        return this.apiService.get(`${controllerRoute}/company-level`).pipe(
            map((response: ResourceDocuments) => {
                this.companyResources = response.documents;
                return response.documents;
            }),
        );
    }

    public getComplianceResources(): Observable<ResourceLink[]> {
        if (this.complianceResources)
            return of(this.complianceResources);

        return this.apiService.get(`${controllerRoute}/compliance-level`).pipe(
            map((response: ResourceDocuments) => {
                this.complianceResources = response.documents;
                return response.documents;
            }),
        );
    }

    public getForm1095s(): Observable<Form1095Model> {
        return this.apiService.get(`${controllerRoute}/form-1095-level`).pipe(
            map((response: Form1095Model) => {
                return response;
            }),
        );
    }
    public getForm1095Download(id: number): Observable<Blob> {
        return this.apiService.get(`${controllerRoute}/form-1095/${id}`, ApiHeaderType.blob).pipe(
            map((response: Blob) => {
                return response;
            })
        );
    }

    /**
     * API only sends a 204
     * @param Form1095EmailCriteria
     * @returns
     */
    public sendForm1095ToEmail(payload: Form1095EmailCriteria): Observable<any> {
        return this.apiService.post(`${controllerRoute}/form-1095-email`, payload).pipe(
            map(() => {
                switch (environment.type) {
                    case EnvironmentType.Prod:
                        this.toastService.buildToastMessage(ToastType.success, 'Email Sent', 'Please check your email shortly.');
                        break;

                    default:
                        this.toastService.buildToastMessage(ToastType.warning, 'Email Notice', 'Email request saved but will not be sent from this environment.');
                        break;
                }
            })
        );
    }


    public getW2s(): Observable<W2Model> {
        return this.apiService.get(`${controllerRoute}/w2-level`).pipe(
            map((response: W2Model) => {
                return response;
            }),
        );
    }
    public getW2Download(id: number): Observable<Blob> {
        return this.apiService.get(`${controllerRoute}/w2/${id}`, ApiHeaderType.blob).pipe(
            map((response: Blob) => {
                return response;
            })
        );
    }

    /**
     * API only sends a 204
     * @param W2EmailCriteria
     * @returns
     */
    public sendW2ToEmail(payload: W2EmailCriteria): Observable<any> {
        return this.apiService.post(`${controllerRoute}/w2-email`, payload).pipe(
            map(() => {
                switch (environment.type) {
                    case EnvironmentType.Prod:
                        this.toastService.buildToastMessage(ToastType.success, 'Email Sent', 'Please check your email shortly.');
                        break;

                    default:
                        this.toastService.buildToastMessage(ToastType.warning, 'Email Notice', 'Email request saved but will not be sent from this environment.');
                        break;
                }
            })
        );
    }
}